/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: <Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/

import { client, timezone } from "./";
import {
  urlToFile,
  getThumbnail,
  getAuthTokenFromSessionStorage,
} from "helpers/common";
import {
  swapViewImgList,
  updateImageDataByID,
  getViewImages,
  getImageById,
  deleteViewImages,
  addOrUpdateTotalBulkImagesCount,
} from "../database/offlineDb/indexDb";
import { getTotalBulkImagesCount } from "../database/offlineDb/indexDb";
import { getBulkRowTuples } from "../realtime/bulkUploadProjection";
import { normalizeViewImagesResponse } from "../realtime/viewImagesResponse";

let viewImagesRequestGeneration = 0;
let viewImagesPersistence = Promise.resolve();

const isCurrentViewImagesRequest = (generation) =>
  generation === viewImagesRequestGeneration;
const beginViewImagesRequest = (dispatch, descriptor) => {
  const generation = ++viewImagesRequestGeneration;
  dispatch({
    type: "BEGIN_VIEW_IMAGES_REQUEST",
    payload: { generation, descriptor },
  });
  return generation;
};
export const serializeViewImagesPersistence = (
  operation,
  isCurrent = () => true
) => {
  const current = viewImagesPersistence
    .catch((error) => {
      console.warn("view-images-action.js previous operation failed, continuing queue", error);
    })
    .then(() => (isCurrent() ? operation() : { outcome: "ignored" }));
  viewImagesPersistence = current.catch((error) => {
    console.warn("view-images-action.js operation failed", error);
  });
  return current;
};
const persistCurrentViewImagesRequest = (generation, operation) =>
  serializeViewImagesPersistence(operation, () =>
    isCurrentViewImagesRequest(generation)
  );

export const retireViewImagesRequests = () => (dispatch) => {
  const generation = ++viewImagesRequestGeneration;
  dispatch({ type: "RETIRE_VIEW_IMAGES_REQUEST", payload: { generation } });
  return { outcome: "ignored" };
};

export const getViewImagesList = () => async (dispatch, getState) => {
  const viewImagesList = await getViewImages();
  const totalBulkImagesCount = await getTotalBulkImagesCount();
  const state = getState?.();
  const activeOrgId = Number(state?.userStore?.userProfile?.orgz_id);
  const descriptor = state?.viewImagesStore?.realtimeDescriptor;
  const currentRows = state?.viewImagesStore?.viewImagesList;
  const hasAuthorizedScope =
    Number.isSafeInteger(activeOrgId) &&
    descriptor?.primaryOrgId === activeOrgId &&
    Number.isSafeInteger(descriptor?.orgId) &&
    Array.isArray(currentRows);
  const authorizedGuids = hasAuthorizedScope
    ? new Set(currentRows.map((row) => row?.guid).filter(Boolean))
    : null;
  const targetOrgId = hasAuthorizedScope ? descriptor.orgId : activeOrgId;
  const scopedRows = Number.isSafeInteger(targetOrgId)
    ? (viewImagesList.data || []).filter(
        (row) =>
          row?.mrn === null &&
          Number(row?.orgz_id) === targetOrgId &&
          (!authorizedGuids || authorizedGuids.has(row?.guid))
      )
    : [];
  let response = dispatch({
    type: "GET_VIEW_IMAGE",
    payload: {
      viewImagesList: scopedRows,
      count: totalBulkImagesCount,
    },
  });
  return response;
};
// export const getViewImageListById=(id)=>dispatch=> {
//     let response=dispatch({
//         type: 'GET_VIEW_IMAGE_BY_ID',
//         payload: getImageById()
//       })
//     return response
// }
// -----for store-----
export function createViewImageListData(value) {
  return (dispatch) => {
    dispatch({
      type: "CREATE_VIEW_IMAGE_LIST",
      payload: value,
    });
  };
}
export function selectedViewImageListData(value) {
  return (dispatch) => {
    dispatch({
      type: "SELECTED_VIEW_IMAGE_LIST",
      payload: value,
    });
  };
}
export function updateViewImageListByID(value, metadata = {}) {
  // console.log("value", value);
  return (dispatch) => {
    dispatch({
      type: "UPDATE_VIEW_IMAGE_LIST_BY_ID",
      payload: value,
      meta: metadata,
    });
  };
}
export function triggerViewImagesReportCountAlert(payload) {
  return {
    type: "TRIGGER_VIEW_IMAGES_REPORT_COUNT_ALERT",
    payload,
  };
}

export const deleteViewImagesAction = (imgList) => {
  return async (dispatch) => {
    try {
      const guidList = imgList.map((row) => row.guid);
      const result = await client.post(
        "/img-delete",
        { guidList },
        {
          headers: {
            authorization: getAuthTokenFromSessionStorage(),
          },
        }
      );

      if (result) {
        await deleteViewImages(result.data.guidList);
        dispatch({
          type: "DELETE_IMAGES_FROM_IDB_AND_STORE",
          payload: {
            guidList: result.data.guidList,
            noOfImagesToDelete: result.data.guidList.length,
          },
        });
      }
    } catch (err) {
      console.log({ error: err });
    }
  };
};

const hydrateViewImagesResponse = async (backendResp) => {
  const normalized = normalizeViewImagesResponse(backendResp);
  const rows = await Promise.all(
    normalized.rows.map(async ({ url, ...row }) => {
      const file = await urlToFile(url, row.guid, {
        lastModified: new Date(`${row.imodon}`).getTime(),
        lastModifiedDate: new Date(`${row.imodon}`),
      });
      const thumbnailUrl = await getThumbnail(file);
      return { ...row, file, thumbnailUrl };
    })
  );
  return { ...normalized, rows };
};

const mergeViewImagesRows = (existingRows, incomingRows) => {
  const rows = new Map();
  [...existingRows, ...incomingRows].forEach((row) => rows.set(row.guid, row));
  return Array.from(rows.values());
};

const responseProcessor = async (
  backendResp,
  dispatch,
  setIsLoading,
  generation,
  descriptor,
  append = false,
  isToGetAllReportDataToPrint = false,
  downloadType = "",
  existingRows = []
) => {
  const prepared = await hydrateViewImagesResponse(backendResp);
  if (!isCurrentViewImagesRequest(generation)) return { outcome: "ignored" };
  return persistCurrentViewImagesRequest(generation, async () => {
    const nextRows = append
      ? mergeViewImagesRows(existingRows, prepared.rows)
      : prepared.rows;
    const trackedRows = nextRows.filter(
      (row) => Array.isArray(row?.status) && row.status.length > 0
    );
    const activeTuples = trackedRows.flatMap(getBulkRowTuples);
    if (
      trackedRows.length > 0 &&
      (activeTuples.length === 0 ||
        activeTuples.length !==
          trackedRows.reduce((count, row) => count + row.status.length, 0) ||
        new Set(activeTuples).size !== activeTuples.length)
    ) {
      throw new Error("Invalid View Images identity evidence");
    }
    const persistedRows = await swapViewImgList(nextRows);
    if (!isCurrentViewImagesRequest(generation)) return { outcome: "ignored" };
    await addOrUpdateTotalBulkImagesCount(prepared.count);
    if (!isCurrentViewImagesRequest(generation)) return { outcome: "ignored" };
    dispatch({
      type: "COMMIT_VIEW_IMAGES_REQUEST",
      payload: {
        generation,
        descriptor: { ...descriptor, activeTuples: [...activeTuples].sort() },
        viewImagesList: persistedRows || nextRows,
        count: prepared.count,
      },
    });
    if (isToGetAllReportDataToPrint) {
      dispatch({
        type: "UPDATE_IMAGES_SELECTED",
        payload: { viewImagesList: persistedRows || nextRows, downloadType },
      });
    }
    setIsLoading(false);
    return { outcome: "applied", rows: persistedRows || nextRows };
  });
};

export const integrateFetchedImgListIntoViewImageList =
  (
    fromDate,
    toDate,
    setIsLoading,
    selectedOrgIdForReview,
    orgz_id,
    persistedRowsPerPage
  ) =>
  async (dispatch) => {
    const reviewOrgId = Number(selectedOrgIdForReview) || null;
    const descriptor = {
      mode: "date",
      fromDate,
      toDate,
      orgId: reviewOrgId || Number(orgz_id),
      primaryOrgId: Number(orgz_id),
      reviewOrgId,
      page: 0,
      pageSize: Number(persistedRowsPerPage || 50),
    };
    const generation = beginViewImagesRequest(dispatch, descriptor);
    try {
      const backendResp = await client.get("/getBulkImagesMonthWise", {
      withCredentials: true,
      headers: {
        Accept: "*/*",
        startdate: fromDate,
        enddate: toDate,
        authorization: getAuthTokenFromSessionStorage(),
        orgid: orgz_id,
        ...(reviewOrgId ? { orgid_for_review: reviewOrgId } : {}),
        skip: 0,
        limit: persistedRowsPerPage || 50,
      },
      });

      return await responseProcessor(
        backendResp,
        dispatch,
        setIsLoading,
        generation,
        descriptor
      );
    } catch (error) {
      if (isCurrentViewImagesRequest(generation)) setIsLoading(false);
      throw error;
    }
  };

export const getBulkImagesMonthWiseAndPageWise =
  (
    fromDate,
    toDate,
    setIsLoading,
    // selectedOrgIdForReview,
    orgz_id,
    limit,
    skip,
    isToGetAllReportDataToPrint = false,
    downloadType = ""
  ) =>
  async (dispatch, getState) => {
    const currentState = getState?.();
    const currentDescriptor = currentState?.viewImagesStore?.realtimeDescriptor;
    const reviewOrgId = currentDescriptor?.reviewOrgId || null;
    const descriptor = {
      mode: "date",
      fromDate,
      toDate,
      orgId: reviewOrgId || Number(orgz_id),
      primaryOrgId: Number(orgz_id),
      reviewOrgId,
      page: Math.floor(Number(skip) / Number(limit)),
      pageSize: Number(limit),
    };
    const canAppendCurrentRows =
      currentDescriptor?.mode === descriptor.mode &&
      currentDescriptor?.fromDate === descriptor.fromDate &&
      currentDescriptor?.toDate === descriptor.toDate &&
      currentDescriptor?.orgId === descriptor.orgId &&
      currentDescriptor?.primaryOrgId === descriptor.primaryOrgId &&
      (currentDescriptor?.reviewOrgId || null) === descriptor.reviewOrgId;
    const existingRows =
      canAppendCurrentRows &&
      Array.isArray(currentState?.viewImagesStore?.viewImagesList)
        ? currentState.viewImagesStore.viewImagesList
        : [];
    const generation = beginViewImagesRequest(dispatch, descriptor);
    try {
    // No need to include the timezone inside headers, when we are using axios client instance.
    const backendResp = await client.get("/getBulkImagesMonthWise", {
      withCredentials: true,
      headers: {
        Accept: "*/*",
        startdate: fromDate,
        enddate: toDate,
        authorization: getAuthTokenFromSessionStorage(),
        orgid: orgz_id,
        orgid_for_review: reviewOrgId || undefined,
        skip,
        limit,
      },
    });

      return await responseProcessor(
        backendResp,
        dispatch,
        setIsLoading,
        generation,
        descriptor,
        true,
        isToGetAllReportDataToPrint,
        downloadType,
        existingRows
      );
    } catch (error) {
      if (isCurrentViewImagesRequest(generation)) setIsLoading(false);
      throw error;
    }
  };

export const searchByImageNameAction =
  (imgName, setIsLoading, orgz_id) => async (dispatch, getState) => {
    const currentDescriptor = getState?.()?.viewImagesStore?.realtimeDescriptor;
    const reviewOrgId = currentDescriptor?.reviewOrgId || null;
    const descriptor = {
      mode: "search",
      query: imgName,
      orgId: reviewOrgId || Number(orgz_id),
      primaryOrgId: Number(orgz_id),
      reviewOrgId,
      page: 0,
      pageSize: 1000,
    };
    const generation = beginViewImagesRequest(dispatch, descriptor);
    try {
      const backendResp = await client.get("/imageSearch", {
      withCredentials: true,
      headers: {
        Accept: "*/*",
        name: imgName,
        authorization: getAuthTokenFromSessionStorage(),
        orgid: orgz_id,
        orgid_for_review: reviewOrgId || undefined,
        timezone,
      },
      });

      return await responseProcessor(
        backendResp,
        dispatch,
        setIsLoading,
        generation,
        descriptor
      );
    } catch (error) {
      if (isCurrentViewImagesRequest(generation)) setIsLoading(false);
      throw error;
    }
  };

export const drConsentAction =
  (
    row,
    guid,
    drConsentDetails,
    viewImagesProps,
    clearStatesInImgManipulator,
    clearSingleImageManipulatorModalDataForViewImageTable,
    setSingleImageManipulatorSnackbarMsg
  ) =>
  async (dispatch) => {
    try {
      const sendData = {
        user_id: viewImagesProps.userDetail.user_id,
        images: [
          {
            drConsentDetails,
            guid,
            Model: viewImagesProps.userStore.userSettings.algorithms,
          },
        ],
      };
      const backendRes = await client.put("/annotation/dr", sendData, {
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
          "Access-Control-Allow-Origin": "*",
        },
      });

      const drConsentDetailsObj = {
        doc_cf: drConsentDetails?.clinicalFeatures,
        doc_diff_dis: drConsentDetails?.differentialDiagnosis,
        doc_dis: drConsentDetails?.primary?.disease,
        doc_dis_catg: drConsentDetails?.primary?.category,
        doc_rec: drConsentDetails?.recommendation,
        doc_ref: drConsentDetails?.referralType,
        doc_sec_fin: drConsentDetails?.secondaryFindings,
      };
      clearStatesInImgManipulator();

      await updateImageDataByID(row.guid, {
        ...row,
        consent: drConsentDetails.primary.disease,
        drConsentDetails: drConsentDetailsObj,
        // ...drConsentDetails,
      });

      const viewImagesFromIdb = await getViewImages();
      await dispatch({
        type: "GET_VIEW_IMAGE_FULFILLED",
        payload: viewImagesFromIdb,
      });
      // const consentHtmlElement = document.getElementById("consent" + guid);
      // if (consentHtmlElement)
      //   consentHtmlElement.innerText = drConsentDetails?.primary?.disease;
      clearSingleImageManipulatorModalDataForViewImageTable();

      setSingleImageManipulatorSnackbarMsg({
        msg: "Modified successfully!",
        variant: "success",
      });
    } catch (err) {
      setSingleImageManipulatorSnackbarMsg({
        msg: "Failed to Modify!",
        variant: "error",
      });
      console.log("view images doc annotation setting failed", err);
    }
  };

export const triggerReview =
  (guid, row, setSnackbarData, authToken) => async (dispatch) => {
    authToken = authToken || getAuthTokenFromSessionStorage();
    try {
      const res = await client.post("/review", guid, {
        headers: {
          Accept: "*/*",
          authorization: authToken,
        },
      });
      const status = {
        ...row.status[0],
        review: 1,
      };
      await updateImageDataByID(row.guid, {
        ...row,
        status: [status],
      });

      const viewImagesFromIdb = await getViewImages();
      await dispatch({
        type: "GET_VIEW_IMAGE_FULFILLED",
        payload: viewImagesFromIdb,
      });
      setSnackbarData({
        msg: res.data.msg,
        variant: "success",
      });
      dispatch({ type: "TRIGGER_REVIEW", payload: res.data });
    } catch (error) {
      setSnackbarData({
        msg: error?.response?.data?.msg || "Error While Triggering The  Review",
        variant: "error",
      });
    }
  };
